import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useStudentProfileStore } from '../../stateManagers/stores/studentProfileStore';

/* ─── Design tokens (matches Assignments.tsx / Dashboard.tsx) ────────── */
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyDark:    '#0a1a3d',
  navyHover:   'rgba(14,34,93,0.06)',
  gold:        '#fbb21f',
  goldLight:   '#ffc93c',
  goldDim:     'rgba(251,178,31,0.18)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  bg:          '#f5f7fa',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.08)',
  redBorder:   'rgba(220,38,38,0.25)',
  amber:       '#f59e0b',
  amberDim:    'rgba(245,158,11,0.12)',
  amberBorder: 'rgba(245,158,11,0.35)',
  blue:        '#2563eb',
  blueDim:     'rgba(37,99,235,0.1)',
  blueBorder:  'rgba(37,99,235,0.25)',
};

/* ─── Types ─────────────────────────────────────────────────────────── */
interface Batch {
  batch_id:    string;
  batch_name:  string;
  course_name?: string | null;
}

type AttendanceStatus = 'present' | 'absent' | 'late' | 'excused';

interface AttendanceRecordItem {
  batch_id:        string;
  course_id:       string | null;
  attendance_date: string;
  topic:           string | null;
  status:          AttendanceStatus;
}

interface AttendanceByStudentResponse {
  success: boolean;
  data: {
    student_id:   string;
    student_name: string;
    summary: {
      total_days: number;
      present:    number;
      absent:     number;
      late:       number;
      excused:    number;
      attendance_percentage: number;
    };
    records: AttendanceRecordItem[];
  };
}

interface BatchAttendanceStat {
  batch_id:    string;
  batch_name:  string;
  total:       number;
  present:     number;
  absent:      number;
  late:        number;
  excused:     number;
  percentage:  number;
}

interface MonthTrendPoint {
  key:        string; // "2026-07"
  label:      string; // "Jul"
  year:       number;
  month:      number; // 0-indexed
  total:      number;
  present:    number;
  absent:     number;
  late:       number;
  excused:    number;
  percentage: number;
}

const STATUS_META: Record<AttendanceStatus, { label: string; color: string; icon: string }> = {
  present: { label: 'Present', color: C.green, icon: 'pi-check-circle' },
  absent:  { label: 'Absent',  color: C.red,   icon: 'pi-times-circle' },
  late:    { label: 'Late',    color: C.gold,  icon: 'pi-clock' },
  excused: { label: 'Excused', color: C.blue,  icon: 'pi-verified' },
};

const MONTH_NAMES  = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT  = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TREND_MONTHS = 6;
const LOW_SAMPLE_THRESHOLD = 3; // months with fewer classes than this show a count instead of a %

/* ─── Helpers ────────────────────────────────────────────────────────── */
const toDateKey = (dateString: string) => {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDateKeyLong = (dateKey: string) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return date.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
};

const percentageColor = (pct: number) => {
  if (pct >= 85) return C.green;
  if (pct >= 65) return C.gold;
  return C.red;
};

/* ─── Donut chart (pure SVG, no chart library) ───────────────────────── */
const DonutChart: React.FC<{ stats: BatchAttendanceStat | null; size?: number; strokeWidth?: number }> = ({ stats, size = 168, strokeWidth = 18 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  const segments: { key: AttendanceStatus; value: number }[] = stats
    ? [
        { key: 'present' as AttendanceStatus, value: stats.present },
        { key: 'late' as AttendanceStatus, value: stats.late },
        { key: 'excused' as AttendanceStatus, value: stats.excused },
        { key: 'absent' as AttendanceStatus, value: stats.absent },
      ].filter(s => s.value > 0)
    : [];

  const total = stats?.total ?? 0;
  let cumulative = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      {total === 0 ? (
        <circle cx={center} cy={center} r={radius} fill="none" stroke="#eef0f3" strokeWidth={strokeWidth} />
      ) : (
        segments.map(seg => {
          const fraction = seg.value / total;
          const dash = fraction * circumference;
          const gap = circumference - dash;
          const offset = -cumulative * circumference;
          cumulative += fraction;
          return (
            <circle
              key={seg.key}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={STATUS_META[seg.key].color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
            />
          );
        })
      )}
    </svg>
  );
};

/* ─── Monthly trend chart (pure divs, no chart library) ──────────────── */
const MonthlyTrendChart: React.FC<{ data: MonthTrendPoint[]; activeKey: string; onSelect: (key: string) => void }> = ({ data, activeKey, onSelect }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '14px', height: '160px', padding: '0 4px' }}>
      {data.map(point => {
        const isActive = point.key === activeKey;
        const attended = point.present + point.late + point.excused;
        const isLowSample = point.total > 0 && point.total < LOW_SAMPLE_THRESHOLD;
        const barColor = point.total === 0 ? '#e5e7eb' : isLowSample ? C.blue : percentageColor(point.percentage);
        const heightPct = point.total === 0 ? 4 : Math.max(6, point.percentage);
        const tooltip = point.total === 0
          ? 'No class recorded — click to view this month'
          : `${attended}/${point.total} attended — P:${point.present} A:${point.absent} L:${point.late} E:${point.excused}`;
        return (
          <div
            key={point.key}
            onClick={() => onSelect(point.key)}
            title={tooltip}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end', cursor: 'pointer' }}
          >
            <span style={{ fontSize: '0.68rem', fontWeight: '800', color: point.total === 0 ? C.muted : barColor, marginBottom: '6px' }}>
              {point.total === 0 ? '—' : isLowSample ? `${attended}/${point.total}` : `${point.percentage}%`}
            </span>
            <div style={{ width: '100%', maxWidth: '38px', height: '100px', display: 'flex', alignItems: 'flex-end', borderRadius: '8px', overflow: 'hidden', background: '#f1f2f4' }}>
              <div
                style={{
                  width: '100%',
                  height: `${heightPct}%`,
                  background: `linear-gradient(180deg, ${barColor}, ${barColor}cc)`,
                  borderRadius: '8px 8px 0 0',
                  border: isActive ? `2px solid ${C.navy}` : 'none',
                  boxSizing: 'border-box',
                  transition: 'height 0.25s ease',
                }}
              />
            </div>
            <span style={{ fontSize: '0.7rem', fontWeight: isActive ? '800' : '600', color: isActive ? C.navy : C.sub, marginTop: '8px' }}>
              {point.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────── */
const Attendance: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { profile, isLoading: profileLoading } = useStudentProfileStore();

  const studentId = profile?.student_id ?? null;

  const batches: Batch[] = (profile?.enrollments ?? [])
    .filter(e => e.batch_id !== null && e.batch_id !== undefined && e.batch_id.trim() !== '')
    .map(e => ({
      batch_id:    e.batch_id as string,
      batch_name:  e.batch_name ?? 'Batch',
      course_name: e.course_name ?? null,
    }));
  const isEnrolled = batches.length > 0;

  const [loading, setLoading] = useState(true);
  const [batchAttendance, setBatchAttendance] = useState<BatchAttendanceStat[]>([]);
  const [recordsByBatch, setRecordsByBatch] = useState<Record<string, AttendanceRecordItem[]>>({});
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [trendAnchor, setTrendAnchor] = useState<Date>(new Date());

  useEffect(() => {
    if (studentId) fetchAttendance();
  }, [studentId]);

  const fetchAttendance = async () => {
    if (!studentId) return;
    try {
      setLoading(true);
      const res = await apiClient.get<AttendanceByStudentResponse>(`/v1/attendance/student/${studentId}`);
      if (res.data.success) {
        const statsMap: Record<string, BatchAttendanceStat> = {};
        const recordsMap: Record<string, AttendanceRecordItem[]> = {};

        for (const rec of res.data.data.records) {
          const enr = batches.find(b => b.batch_id === rec.batch_id);
          if (!statsMap[rec.batch_id]) {
            statsMap[rec.batch_id] = {
              batch_id: rec.batch_id,
              batch_name: enr?.batch_name ?? 'Batch',
              total: 0, present: 0, absent: 0, late: 0, excused: 0, percentage: 0,
            };
          }
          statsMap[rec.batch_id].total += 1;
          statsMap[rec.batch_id][rec.status] += 1;

          if (!recordsMap[rec.batch_id]) recordsMap[rec.batch_id] = [];
          recordsMap[rec.batch_id].push(rec);
        }

        const enrolledBatchIds = batches.map(b => b.batch_id);
        const allBatchIds = Array.from(new Set([...enrolledBatchIds, ...Object.keys(statsMap)]));

        const stats = allBatchIds.map(bId => {
          const enr = batches.find(b => b.batch_id === bId);
          const existing = statsMap[bId];
          if (existing) {
            return {
              ...existing,
              batch_name: enr?.batch_name ?? existing.batch_name,
              percentage: existing.total > 0
                ? Math.round(((existing.present + existing.late + existing.excused) / existing.total) * 100)
                : 0,
            };
          }
          return {
            batch_id: bId,
            batch_name: enr?.batch_name ?? 'Batch',
            total: 0, present: 0, absent: 0, late: 0, excused: 0, percentage: 0,
          };
        });

        // Sort each batch's records chronologically for consistent trend/table ordering
        Object.values(recordsMap).forEach(list => list.sort((a, b) => new Date(a.attendance_date).getTime() - new Date(b.attendance_date).getTime()));

        setBatchAttendance(stats);
        setRecordsByBatch(recordsMap);
        if (stats.length > 0) setSelectedBatchId(prev => (prev && stats.some(s => s.batch_id === prev)) ? prev : stats[0].batch_id);
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load attendance', life: 3000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to load attendance', life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSelect = (batchId: string) => {
    setSelectedBatchId(batchId);
    setSelectedDayKey(null);
  };

  /* ── Derived data ─────────────────────────────────────────────────── */
  const selectedBatchMeta = batchAttendance.find(s => s.batch_id === selectedBatchId) ?? null;
  const selectedRecords = selectedBatchId ? (recordsByBatch[selectedBatchId] ?? []) : [];

  const dayRecordMap = useMemo(() => {
    const map: Record<string, AttendanceRecordItem> = {};
    for (const rec of selectedRecords) map[toDateKey(rec.attendance_date)] = rec;
    return map;
  }, [selectedRecords]);

  // Calendar-month stats — the donut + breakdown reflect only the month in view.
  const monthStat: BatchAttendanceStat | null = useMemo(() => {
    if (!selectedBatchId) return null;
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const counts: Record<AttendanceStatus, number> = { present: 0, absent: 0, late: 0, excused: 0 };
    let total = 0;

    for (const key in dayRecordMap) {
      const [y, m] = key.split('-').map(Number);
      if (y === year && m === month + 1) {
        counts[dayRecordMap[key].status] += 1;
        total += 1;
      }
    }

    const percentage = total > 0 ? Math.round(((counts.present + counts.late + counts.excused) / total) * 100) : 0;
    return { batch_id: selectedBatchId, batch_name: selectedBatchMeta?.batch_name ?? 'Batch', total, ...counts, percentage };
  }, [selectedBatchId, dayRecordMap, calendarDate, selectedBatchMeta]);

  const calendarCells = useMemo(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayKey = toDateKey(new Date().toISOString());

    const cells: { day: number | null; key: string | null; status: AttendanceStatus | null; topic: string | null; isToday: boolean }[] = [];
    for (let i = 0; i < firstWeekday; i++) cells.push({ day: null, key: null, status: null, topic: null, isToday: false });
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const rec = dayRecordMap[key];
      cells.push({ day: d, key, status: rec?.status ?? null, topic: rec?.topic ?? null, isToday: key === todayKey });
    }
    return cells;
  }, [calendarDate, dayRecordMap]);

  const selectedDayRecord = selectedDayKey ? dayRecordMap[selectedDayKey] ?? null : null;

  const shiftMonth = (delta: number) => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    setSelectedDayKey(null);
  };

  const handleDayClick = (key: string | null) => {
    if (!key) return;
    setSelectedDayKey(prev => (prev === key ? null : key));
  };

  // Monthly trend — last TREND_MONTHS months ending at trendAnchor, for the selected batch.
  const monthlyTrend: MonthTrendPoint[] = useMemo(() => {
    const points: MonthTrendPoint[] = [];
    for (let i = TREND_MONTHS - 1; i >= 0; i--) {
      const d = new Date(trendAnchor.getFullYear(), trendAnchor.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth();
      const counts: Record<AttendanceStatus, number> = { present: 0, absent: 0, late: 0, excused: 0 };
      let total = 0;
      for (const rec of selectedRecords) {
        const recDate = new Date(rec.attendance_date);
        if (recDate.getFullYear() === year && recDate.getMonth() === month) {
          counts[rec.status] += 1;
          total += 1;
        }
      }
      const percentage = total > 0 ? Math.round(((counts.present + counts.late + counts.excused) / total) * 100) : 0;
      points.push({ key: `${year}-${String(month + 1).padStart(2, '0')}`, label: MONTH_SHORT[month], year, month, total, ...counts, percentage });
    }
    return points;
  }, [selectedRecords, trendAnchor]);

  const trendAverage = useMemo(() => {
    const withData = monthlyTrend.filter(p => p.total > 0);
    if (withData.length === 0) return 0;
    return Math.round(withData.reduce((sum, p) => sum + p.percentage, 0) / withData.length);
  }, [monthlyTrend]);

  const handleTrendSelect = (key: string) => {
    const [y, m] = key.split('-').map(Number);
    setCalendarDate(new Date(y, m - 1, 1));
    setSelectedDayKey(null);
  };

  const shiftTrend = (delta: number) => {
    setTrendAnchor(prev => new Date(prev.getFullYear(), prev.getMonth() + delta * TREND_MONTHS, 1));
  };

  // Recent records for the month in view, most recent first.
  const monthRecords = useMemo(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    return selectedRecords
      .filter(rec => {
        const d = new Date(rec.attendance_date);
        return d.getFullYear() === year && d.getMonth() === month;
      })
      .slice()
      .sort((a, b) => new Date(b.attendance_date).getTime() - new Date(a.attendance_date).getTime());
  }, [selectedRecords, calendarDate]);

  const overallPercentage = selectedBatchMeta?.percentage ?? 0;

  /* ── Guard renders ────────────────────────────────────────────────── */
  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '12px' }}>
        <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading profile…</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .att-batch-chips { display: flex; gap: 6px; flex-wrap: wrap; overflow-x: auto; padding-bottom: 2px; }
        .att-batch-chips::-webkit-scrollbar { height: 3px; }
        .att-batch-chips::-webkit-scrollbar-track { background: transparent; }
        .att-batch-chips::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
        .att-batch-chip { white-space: nowrap; transition: all 0.15s ease; }
        .att-batch-chip:hover { transform: translateY(-1px); }
        .att-cal-nav-btn { transition: all 0.15s ease; }
        .att-cal-nav-btn:hover { background: ${C.navyHover} !important; border-color: rgba(14,34,93,0.25) !important; }
        .att-cal-cell { transition: transform 0.1s ease, box-shadow 0.1s ease; cursor: pointer; }
        .att-cal-cell:hover { transform: scale(1.08); box-shadow: 0 2px 6px rgba(14,34,93,0.18); }
        .att-cal-cell:active { transform: scale(0.95); }
        .att-day-detail { animation: attFadeIn 0.15s ease; }
        .att-stat-tile { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .att-stat-tile:hover { transform: translateY(-2px); box-shadow: 0 4px 10px rgba(14,34,93,0.08); }
        .att-record-row { transition: background 0.12s ease; }
        .att-record-row:hover { background: rgba(14,34,93,0.025) !important; }
        @keyframes attFadeIn { from { opacity: 0; transform: translateY(-2px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes att-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      <Toast ref={toast} />

      <div style={{ background: C.bg, minHeight: '100%' }}>

        {/* ── Page header ─────────────────────────────────────────── */}
        <div style={{
          background:   `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 60%, ${C.navyDark} 100%)`,
          borderRadius: '20px',
          padding:      '18px 24px',
          marginBottom: '16px',
          color:        C.white,
          position:     'relative',
          overflow:     'hidden',
          boxShadow:    '0 4px 18px rgba(14,34,93,0.25)',
        }}>
          <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 10px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '6px', fontSize: '0.64rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
                <i className="pi pi-calendar" style={{ fontSize: '0.66rem' }} />
                Attendance
              </div>
              <h1 style={{ margin: '0 0 3px 0', fontSize: '1.3rem', fontWeight: '700', color: C.white, letterSpacing: '-0.4px' }}>
                Attendance
              </h1>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem' }}>
                {selectedBatchMeta && selectedBatchMeta.total > 0
                  ? `${selectedBatchMeta.total} classes recorded for ${selectedBatchMeta.batch_name}`
                  : 'Your attendance record across all batches'}
              </p>
            </div>

            {selectedBatchMeta && selectedBatchMeta.total > 0 && (
              <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', padding: '9px 16px', textAlign: 'center', backdropFilter: 'blur(10px)' }}>
                <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: C.gold, lineHeight: 1 }}>{overallPercentage}%</p>
                <p style={{ margin: '3px 0 0 0', fontSize: '0.64rem', color: 'rgba(255,255,255,0.6)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Overall</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Batch filter chips ───────────────────────────────────── */}
        {(batches.length > 0 || loading) && (
          <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '9px 14px', marginBottom: '14px', boxShadow: '0 1px 4px rgba(14,34,93,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                <i className="pi pi-users" style={{ fontSize: '0.72rem', color: C.muted }} />
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Batch</span>
              </div>
              <div style={{ width: '1px', height: '16px', background: C.border, flexShrink: 0 }} />

              {loading ? (
                <div style={{ display: 'flex', gap: '6px' }}>
                  {[80, 110, 95].map((w, i) => (
                    <div key={i} style={{ width: `${w}px`, height: '28px', borderRadius: '20px', background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'att-shimmer 1.4s infinite' }} />
                  ))}
                </div>
              ) : (
                <div className="att-batch-chips">
                  {batchAttendance.map(stat => {
                    const isActive = stat.batch_id === selectedBatchId;
                    return (
                      <button
                        key={stat.batch_id}
                        className="att-batch-chip"
                        onClick={() => handleBatchSelect(stat.batch_id)}
                        style={{
                          padding: '4px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer',
                          border: isActive ? 'none' : `1px solid ${C.border}`,
                          background: isActive ? C.navy : 'transparent',
                          color: isActive ? C.gold : C.sub,
                          boxShadow: isActive ? '0 2px 8px rgba(14,34,93,0.2)' : 'none',
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                        }}
                      >
                        {isActive && <i className="pi pi-check" style={{ fontSize: '0.62rem' }} />}
                        {stat.batch_name}
                        {stat.total > 0 && (
                          <span style={{ fontSize: '0.65rem', fontWeight: '700', opacity: 0.75 }}>· {stat.percentage}%</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Not enrolled notice ─────────────────────────────────── */}
        {!loading && !isEnrolled && (
          <div style={{ display: 'flex', gap: '12px', padding: '11px 14px', background: 'rgba(8,145,178,0.08)', border: '1px solid rgba(8,145,178,0.25)', borderLeft: '4px solid #0891b2', borderRadius: '12px', marginBottom: '14px' }}>
            <i className="pi pi-info-circle" style={{ fontSize: '1.1rem', color: '#0891b2', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: '0 0 2px 0', fontSize: '0.83rem', fontWeight: '700', color: C.navy }}>Not Enrolled in a Batch</p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#0891b2', lineHeight: 1.5 }}>
                Attendance is tracked per batch. Enroll in a batch to start seeing records here.
              </p>
            </div>
          </div>
        )}

        {/* ── Loading state ────────────────────────────────────────── */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '320px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '70px', height: '70px', borderRadius: '35px', background: '#E8EEF7', border: `2px solid ${C.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="3" animationDuration=".8s" />
              </div>
              <p style={{ margin: 0, color: C.navy, fontSize: '0.92rem', fontWeight: '600' }}>Loading attendance…</p>
            </div>
          </div>
        ) : !selectedBatchId ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '320px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '52px 26px', textAlign: 'center' }}>
            <div style={{ width: '70px', height: '70px', borderRadius: '35px', background: '#E8EEF7', border: `2px solid ${C.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
              <i className="pi pi-calendar-times" style={{ fontSize: '1.6rem', color: C.navy }} />
            </div>
            <p style={{ margin: '0 0 6px 0', fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>No Attendance Records</p>
            <p style={{ margin: 0, color: C.muted, fontSize: '0.85rem', lineHeight: 1.6, maxWidth: '280px' }}>
              Attendance for your batches will show up here once your teacher starts marking it.
            </p>
          </div>
        ) : (
          <>
            {/* ── Overall stat tiles for the selected batch (area reduced ~40%) ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '5px', marginBottom: '8px' }}>
              <div className="att-stat-tile" style={{ padding: '6px 10px', borderRadius: '9px', border: `1px solid ${C.border}`, background: C.white, boxShadow: '0 1px 4px rgba(14,34,93,0.05)' }}>
                <p style={{ margin: '0 0 2px 0', fontSize: '0.56rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Total Classes</p>
                <p style={{ margin: 0, fontSize: '0.92rem', color: C.navy, fontWeight: '800' }}>{selectedBatchMeta?.total ?? 0}</p>
              </div>
              {(Object.keys(STATUS_META) as AttendanceStatus[]).map(key => {
                const meta = STATUS_META[key];
                const count = selectedBatchMeta ? (selectedBatchMeta as any)[key] as number : 0;
                return (
                  <div key={key} className="att-stat-tile" style={{ padding: '6px 10px', borderRadius: '9px', border: `1px solid ${meta.color}40`, background: `${meta.color}0d` }}>
                    <p style={{ margin: '0 0 2px 0', fontSize: '0.56rem', color: meta.color, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <i className={`pi ${meta.icon}`} style={{ fontSize: '0.52rem' }} /> {meta.label}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.92rem', color: meta.color, fontWeight: '800' }}>{count}</p>
                  </div>
                );
              })}
              <div className="att-stat-tile" style={{ padding: '6px 10px', borderRadius: '9px', border: `1px solid ${C.goldBorder}`, background: C.goldDim }}>
                <p style={{ margin: '0 0 2px 0', fontSize: '0.56rem', color: '#a06000', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Overall %</p>
                <p style={{ margin: 0, fontSize: '0.92rem', color: '#a06000', fontWeight: '800' }}>{overallPercentage}%</p>
              </div>
            </div>

            {/* ── Calendar + Donut ─────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1.3fr) minmax(280px, 1fr)', gap: '14px', marginBottom: '14px', alignItems: 'start' }}>

              {/* Calendar card (padding + spacing reduced ~40%) */}
              <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '3px', height: '16px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
                    <span style={{ fontSize: '0.88rem', fontWeight: '700', color: C.navy }}>Calendar</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <button className="att-cal-nav-btn" onClick={() => shiftMonth(-1)} style={{ width: '24px', height: '24px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                      <i className="pi pi-chevron-left" style={{ fontSize: '0.6rem' }} />
                    </button>
                    <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, minWidth: '110px', textAlign: 'center' }}>
                      {MONTH_NAMES[calendarDate.getMonth()]} {calendarDate.getFullYear()}
                    </span>
                    <button className="att-cal-nav-btn" onClick={() => shiftMonth(1)} style={{ width: '24px', height: '24px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                      <i className="pi pi-chevron-right" style={{ fontSize: '0.6rem' }} />
                    </button>
                  </div>
                </div>

                <div style={{ maxWidth: '340px', margin: '0 auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '5px' }}>
                  {WEEKDAY_SHORT.map(w => (
                    <div key={w} style={{ textAlign: 'center', fontSize: '0.78rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {w}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                  {calendarCells.map((cell, idx) => {
                    if (cell.day === null) return <div key={`b${idx}`} />;
                    const meta = cell.status ? STATUS_META[cell.status] : null;
                    const isSelected = cell.key === selectedDayKey;
                    return (
                      <button
                        key={cell.key}
                        type="button"
                        className="att-cal-cell"
                        onClick={() => handleDayClick(cell.key)}
                        title={cell.status ? STATUS_META[cell.status].label : 'No class'}
                        style={{
                          aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: '8px', fontSize: '0.86rem', fontWeight: '700', fontFamily: 'inherit',
                          background: meta ? `${meta.color}1a` : '#f7f8fa',
                          color: meta ? meta.color : C.muted,
                          border: isSelected ? `2px solid ${C.navy}` : cell.isToday ? `2px solid ${C.goldBorder}` : '1px solid transparent',
                          padding: 0,
                        }}
                      >
                        {cell.day}
                      </button>
                    );
                  })}
                </div>
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px', paddingTop: '9px', borderTop: `1px solid ${C.border}` }}>
                  {(Object.keys(STATUS_META) as AttendanceStatus[]).map(key => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.68rem', color: C.sub, fontWeight: '600' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '3px', background: STATUS_META[key].color, display: 'inline-block' }} />
                      {STATUS_META[key].label}
                    </div>
                  ))}
                </div>

                {/* Selected day detail */}
                {selectedDayKey && (
                  <div
                    className="att-day-detail"
                    style={{
                      marginTop: '9px', padding: '8px 10px', borderRadius: '9px',
                      background: selectedDayRecord ? `${STATUS_META[selectedDayRecord.status].color}0d` : '#f7f8fa',
                      border: `1px solid ${selectedDayRecord ? `${STATUS_META[selectedDayRecord.status].color}40` : C.border}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <span style={{ fontSize: '0.74rem', fontWeight: '700', color: C.navy }}>{formatDateKeyLong(selectedDayKey)}</span>
                      {selectedDayRecord ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.65rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', color: STATUS_META[selectedDayRecord.status].color, background: `${STATUS_META[selectedDayRecord.status].color}1a` }}>
                          <i className={`pi ${STATUS_META[selectedDayRecord.status].icon}`} style={{ fontSize: '0.6rem' }} />
                          {STATUS_META[selectedDayRecord.status].label}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.65rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', color: C.muted, background: '#eceef1' }}>No class</span>
                      )}
                    </div>
                    {selectedDayRecord?.topic && (
                      <p style={{ margin: '5px 0 0 0', fontSize: '0.72rem', color: C.sub, lineHeight: 1.5 }}>
                        <i className="pi pi-book" style={{ fontSize: '0.65rem', marginRight: '6px', color: C.muted }} />
                        {selectedDayRecord.topic}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Donut + breakdown card */}
              <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', padding: '20px 22px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <div style={{ width: '3px', height: '18px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
                  <span style={{ fontSize: '0.95rem', fontWeight: '700', color: C.navy }}>{MONTH_SHORT[calendarDate.getMonth()]} {calendarDate.getFullYear()} Summary</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '18px' }}>
                  <div style={{ position: 'relative', width: '168px', height: '168px' }}>
                    <DonutChart stats={monthStat} size={168} strokeWidth={18} />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      {(() => {
                        const total = monthStat?.total ?? 0;
                        const attended = (monthStat?.present ?? 0) + (monthStat?.late ?? 0) + (monthStat?.excused ?? 0);
                        const isLowSample = total > 0 && total < LOW_SAMPLE_THRESHOLD;
                        return (
                          <span style={{ fontSize: isLowSample ? '1.3rem' : '1.6rem', fontWeight: '800', color: isLowSample ? C.navy : percentageColor(monthStat?.percentage ?? 0), letterSpacing: '-0.5px' }}>
                            {total === 0 ? '0%' : isLowSample ? `${attended}/${total}` : `${monthStat?.percentage ?? 0}%`}
                          </span>
                        );
                      })()}
                      <span style={{ fontSize: '0.64rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                        {monthStat?.total ?? 0} {monthStat?.total === 1 ? 'class' : 'classes'}
                      </span>
                      {monthStat && monthStat.total > 0 && monthStat.total < LOW_SAMPLE_THRESHOLD && (
                        <span style={{ fontSize: '0.56rem', color: C.muted, fontWeight: '600', marginTop: '2px' }}>too few for %</span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', flex: 1 }}>
                  {(Object.keys(STATUS_META) as AttendanceStatus[]).map(key => {
                    const count = monthStat ? (monthStat as any)[key] as number : 0;
                    const pct = monthStat && monthStat.total > 0 ? Math.round((count / monthStat.total) * 100) : 0;
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '9px', height: '9px', borderRadius: '3px', background: STATUS_META[key].color, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.78rem', color: C.sub, fontWeight: '600', flex: 1 }}>{STATUS_META[key].label}</span>
                        <span style={{ fontSize: '0.78rem', color: C.navy, fontWeight: '700' }}>{count}</span>
                        <span style={{ fontSize: '0.7rem', color: C.muted, fontWeight: '600', minWidth: '34px', textAlign: 'right' }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Monthly trend chart ──────────────────────────────── */}
            <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', padding: '20px 22px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '3px', height: '18px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
                  <span style={{ fontSize: '0.95rem', fontWeight: '700', color: C.navy }}>Monthly Trend</span>
                  <span style={{ padding: '2px 9px', background: C.navyHover, borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', color: C.navy }}>
                    Avg {trendAverage}%
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button className="att-cal-nav-btn" onClick={() => shiftTrend(-1)} style={{ width: '26px', height: '26px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                    <i className="pi pi-chevron-left" style={{ fontSize: '0.62rem' }} />
                  </button>
                  <span style={{ fontSize: '0.72rem', color: C.muted, fontWeight: '600' }}>
                    {monthlyTrend[0]?.label} {monthlyTrend[0]?.year} – {monthlyTrend[monthlyTrend.length - 1]?.label} {monthlyTrend[monthlyTrend.length - 1]?.year}
                  </span>
                  <button className="att-cal-nav-btn" onClick={() => shiftTrend(1)} style={{ width: '26px', height: '26px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                    <i className="pi pi-chevron-right" style={{ fontSize: '0.62rem' }} />
                  </button>
                </div>
              </div>

              <MonthlyTrendChart
                data={monthlyTrend}
                activeKey={`${calendarDate.getFullYear()}-${String(calendarDate.getMonth() + 1).padStart(2, '0')}`}
                onSelect={handleTrendSelect}
              />

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '18px', paddingTop: '14px', borderTop: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', color: C.sub, fontWeight: '600' }}>
                  <span style={{ width: '9px', height: '9px', borderRadius: '3px', background: C.green, display: 'inline-block' }} /> ≥ 85% Good
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', color: C.sub, fontWeight: '600' }}>
                  <span style={{ width: '9px', height: '9px', borderRadius: '3px', background: C.gold, display: 'inline-block' }} /> 65–84% Fair
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', color: C.sub, fontWeight: '600' }}>
                  <span style={{ width: '9px', height: '9px', borderRadius: '3px', background: C.red, display: 'inline-block' }} /> &lt; 65% Low
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', color: C.sub, fontWeight: '600' }}>
                  <span style={{ width: '9px', height: '9px', borderRadius: '3px', background: C.blue, display: 'inline-block' }} /> &lt; {LOW_SAMPLE_THRESHOLD} classes — shown as count
                </div>
              </div>
            </div>

            {/* ── Records list for the month in view ──────────────── */}
            <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '3px', height: '18px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
                  <span style={{ fontSize: '0.95rem', fontWeight: '700', color: C.navy }}>
                    Records — {MONTH_NAMES[calendarDate.getMonth()]} {calendarDate.getFullYear()}
                  </span>
                </div>
                <span style={{ fontSize: '0.76rem', color: C.muted, fontWeight: '600' }}>{monthRecords.length} entries</span>
              </div>

              {monthRecords.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                  <i className="pi pi-calendar" style={{ fontSize: '1.6rem', color: C.navy, opacity: 0.18 }} />
                  <p style={{ marginTop: '10px', color: C.muted, fontSize: '0.85rem' }}>No records for this month</p>
                </div>
              ) : (
                <div>
                  {monthRecords.map((rec, idx) => {
                    const key = toDateKey(rec.attendance_date);
                    const meta = STATUS_META[rec.status];
                    return (
                      <div
                        key={`${key}-${idx}`}
                        className="att-record-row"
                        onClick={() => handleDayClick(key)}
                        style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '13px 22px', borderTop: idx === 0 ? 'none' : `1px solid #f3f4f6`, cursor: 'pointer' }}
                      >
                        <div style={{ width: '38px', height: '38px', flexShrink: 0, borderRadius: '10px', background: `${meta.color}1a`, border: `1px solid ${meta.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className={`pi ${meta.icon}`} style={{ fontSize: '0.9rem', color: meta.color }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: '0 0 3px 0', fontSize: '0.82rem', fontWeight: '700', color: C.body }}>
                            {formatDateKeyLong(key)}
                          </p>
                          {rec.topic && (
                            <p style={{ margin: 0, fontSize: '0.76rem', color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {rec.topic}
                            </p>
                          )}
                        </div>
                        <span style={{ flexShrink: 0, fontSize: '0.7rem', fontWeight: '700', padding: '4px 11px', borderRadius: '20px', color: meta.color, background: `${meta.color}1a` }}>
                          {meta.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Attendance;